import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'

import {
  buildConsentState,
  getConsentCookie,
  hasGpcSignal,
  injectMetaPixel,
  removeMetaPixelCookies,
  setConsentCookie,
  updateGtagConsent,
} from './consentUtils'
import { ConsentContextValue, ConsentState } from './types'

const ConsentContext = createContext<ConsentContextValue | null>(null)

interface CookieConsentProviderProps {
  children: ReactNode
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [consent, setConsent] = useState<ConsentState | null>(null)
  const [mounted, setMounted] = useState(false)
  const [preferencesOpen, setPreferencesOpen] = useState(false)

  // Initialize consent from cookie on mount
  useEffect(() => {
    const saved = getConsentCookie()

    if (saved) {
      setConsent(saved)
      updateGtagConsent(saved.categories.analytics, saved.categories.marketing)

      if (saved.categories.marketing && !hasGpcSignal()) {
        injectMetaPixel()
      }
    }

    setMounted(true)
  }, [])

  const applyConsent = useCallback((analytics: boolean, marketing: boolean) => {
    // Respect GPC signal for marketing
    const effectiveMarketing = hasGpcSignal() ? false : marketing

    const state = buildConsentState(analytics, effectiveMarketing)

    setConsent(state)
    setConsentCookie(state)
    updateGtagConsent(analytics, effectiveMarketing)

    if (effectiveMarketing) {
      injectMetaPixel()
    } else {
      removeMetaPixelCookies()
    }
  }, [])

  const acceptAll = useCallback(() => {
    applyConsent(true, true)
  }, [applyConsent])

  const rejectNonEssential = useCallback(() => {
    applyConsent(false, false)
  }, [applyConsent])

  const updateConsent = useCallback((partial: Partial<{ analytics: boolean; marketing: boolean }>) => {
    const current = consent?.categories ?? { essential: true as const, analytics: false, marketing: false }

    applyConsent(
      partial.analytics ?? current.analytics,
      partial.marketing ?? current.marketing,
    )
  }, [consent, applyConsent])

  const openPreferences = useCallback(() => {
    setPreferencesOpen(true)
  }, [])

  const value = useMemo<ConsentContextValue & { preferencesOpen: boolean; closePreferences: () => void }>(() => ({
    consent: mounted ? consent : undefined as unknown as ConsentState | null,
    hasConsented: mounted && consent !== null,
    isAnalyticsAllowed: mounted && (consent?.categories.analytics ?? false),
    isMarketingAllowed: mounted && (consent?.categories.marketing ?? false),
    acceptAll,
    rejectNonEssential,
    updateConsent,
    openPreferences,
    preferencesOpen,
    closePreferences: () => setPreferencesOpen(false),
  }), [mounted, consent, acceptAll, rejectNonEssential, updateConsent, openPreferences, preferencesOpen])

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>
}

export function useConsent(): ConsentContextValue & { preferencesOpen: boolean; closePreferences: () => void } {
  const context = useContext(ConsentContext)

  if (!context) {
    throw new Error('useConsent must be used within a CookieConsentProvider')
  }

  return context as ConsentContextValue & { preferencesOpen: boolean; closePreferences: () => void }
}
